import { Strophe, $pres, $msg, $iq, $build } from 'strophe.js';
const muc = require('strophejs-plugin-muc');

const logger = message => console.log(message);

const uuid = () => {
    let dt = new Date().getTime();
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        let r = (dt + Math.random() * 16) % 16 | 0;
        dt = Math.floor(dt / 16);
        return (c === 'x' ? r : (r && 0x3 | 0x8)).toString(16);
    });
    return uuid;
}

export const XMPP = { ...Strophe, logger, uuid };

export class XMPPClient {
    constructor(url, domain) {
        this.url = url;
        this.domain = domain;
        this.connection = null;
        this.callbacks = {
            connecting: null,
            connectFailed: null,
            connected: null,
            disconnecting: null,
            disconnected: null,
            authFailed: null,
            message: null,
            error: null
        };

        this.createConnection = this.createConnection.bind(this);
        this.connect = this.connect.bind(this);
        this.onConnecting = this.onConnecting.bind(this);
        this.onConnected = this.onConnected.bind(this);
        this.onConnectFailed = this.onConnectFailed.bind(this);
        this.onDisconnecting = this.onDisconnecting.bind(this);
        this.onDisconnected = this.onDisconnected.bind(this);
        this.onPresence = this.onPresence.bind(this);
        this.onSubscription = this.onSubscription.bind(this);
        this.onMessage = this.onMessage.bind(this);
        this.onError = this.onError.bind(this);
        this.sendMessage = this.sendMessage.bind(this);
        this.history = this.history.bind(this);
        this.disconnect = this.disconnect.bind(this);
    }

    connect(joinId, password) {
        if (!this.url) return;
        const me = this;
        this.createConnection();
        this.connection.connect(joinId, password, status => {
            if (status === Strophe.Status.CONNECTING && me.callbacks.connecting)
                me.callbacks.connecting();
            else if (status === Strophe.Status.CONNECTED && me.callbacks.connected) {
                this.connection.muc.init(this.connection);
                me.callbacks.connected();
            }
            else if (status === Strophe.Status.CONNFAIL && me.callbacks.connectFailed)
                me.callbacks.connectFailed();
            else if (status === Strophe.Status.AUTHFAIL && me.callbacks.authFailed)
                me.callbacks.authFailed();
            else if (status === Strophe.Status.DISCONNECTING && me.callbacks.disconnecting)
                me.callbacks.disconnecting();
            else if (status === Strophe.Status.DISCONNECTED && me.callbacks.disconnected)
                me.callbacks.disconnected();
        });
        return this;
    }

    createConnection() {
        this.connection = new Strophe.Connection(this.url);
        Strophe.addConnectionPlugin("muc", muc);
        return this;
    }

    onConnecting(cb) {
        this.callbacks.connecting = cb;
    }

    onConnected(cb) {
        this.callbacks.connected = cb;
    }

    onConnectFailed(cb) {
        this.callbacks.connectFailed = cb;
    }

    onAuthFailed(cb) {
        this.callbacks.authFailed = cb;
    }

    onDisconnecting(cb) {
        this.callbacks.disconnecting = cb;
    }

    onDisconnected(cb) {
        this.callbacks.disconnected = cb;
    }

    onError(cb) {
        this.callbacks.error = cb;
    }

    disconnect() {
        if (!this.connection) return;
        this.connection.disconnect();
        this.connection._doDisconnect();
        this.connection = null;
    }

    sendMessage(message) {
        const { from, to, } = message;
        let { body } = message;

        if (!this.isConference(to)) {
            const xml = $msg({ from, to, type: 'chat' }).c("body").t(JSON.stringify(body));
            this.connection.send(xml);
        } else {
            this.connection.muc.message(to, null, JSON.stringify(body));
        }
    }

    join(to, nick, messageCallback, params) {
        if (this.isConference(to)) this.connection.muc.join(to, nick, messageCallback, null);
        else this.history(to, params, messageCallback);
    }

    leave(to, nick, leaveCallback) {
        if (this.isConference(to)) this.connection.muc.leave(to, nick, leaveCallback, null);
    }

    history(to, params, messageHandler) {
        const namespace = 'urn:xmpp:mam:1';
        const tool = 'http://jabber.org/protocol/rsm';

        //Initializing IQ
        const from = Strophe.getBareJidFromJid(this.connection.jid);
        const iq = $iq({ to: from, type: 'set', xmlns: 'jabber:client' })
            .c('query', { xmlns: namespace }).c('x', { xmlns: 'jabber:x:data', type: 'submit' });

        //Options for archive message retrieval
        params = params || {};
        const reqParams = { max: 100, before: "", first: -1, index: -1, count: -1, ...params };

        //combine field doms with IQ
        iq.c('field', { var: 'FORM_TYPE', type: 'hidden' }).c('value').t(namespace).up().up()
            .c('field', { var: 'with' }).c('value').t(to).up().up().up();

        //combine RSM node with IQ
        const attrList = ['max', 'first', 'last', 'after', 'before', 'index', 'count'];
        const xml = $build('set', { xmlns: tool });
        for (let i = 0; i < attrList.length; i++) {
            const attrs = reqParams[attrList[i]];
            if ((attrs || attrs === "") && attrs !== null) {
                xml.c(attrList[i]).t((attrs).toString()).up();
            }
        }
        iq.cnode(xml.tree());

        //Sending query using IQ to get specified user's archive messages
        this.connection.sendIQ(iq, messageHandler, this.onError);
    }

    setPresence() {
        this.connection.send($pres());
    }

    onPresence(cb) {
        this.addHandler(cb, null, "presence");
    }

    onSubscription(cb) {
        this.addHandler(cb, null, "presence", "subscribe");
    }

    onMessage(cb, type) {
        this.addHandler(xml => {
            const json = this.messageParser(xml);
            return cb(json, xml);
        }, null, "message", type);
    }

    addHandler(handler, ns, name, type) {
        this.connection.addHandler(handler, ns, name, type);
    }

    getIdToJid(id, conf = false) {
        return conf ? `${id}@conference.${this.domain}` : `${id}@${this.domain}`;
    }

    isConference(jId) {
        return (jId.indexOf("@conference.") >= 0);
    }

    messageParser(xml) {
        if (xml && xml.getElementsByTagName('forwarded').length > 0) {
            const element = xml.childNodes[0];
            const forwarded = element.getElementsByTagName('forwarded');
            if (forwarded.length > 0) {
                const messages = forwarded[0].getElementsByTagName('message');
                xml = messages[0];
            }
        }

        let jsonResult = {
            from: null, fromId: null, to: null, toId: null, authorId: null,
            type: null, chatType: null, messageType: null, body: null
        };
        const msgElement = xml;
        const from = msgElement.getAttribute('from');
        const to = msgElement.getAttribute('to');
        const type = msgElement.getAttribute('type');

        jsonResult = { ...jsonResult, from, to, type };
        if (!(from && to)) return jsonResult;
        if (["chat", "groupchat"].indexOf(type) < 0) return jsonResult;

        const findBody = msgElement.getElementsByTagName('body');
        if (findBody.length === 0) return jsonResult;

        const bodyElement = findBody[0];
        const body = bodyElement.innerHTML ? this.parseJSON(bodyElement.innerHTML) : null;
        if (!body) return { ...jsonResult, body };

        const currentChatId = XMPP.getNodeFromJid(this.connection.jid);
        let fromId = XMPP.getNodeFromJid(from);
        let toId = XMPP.getNodeFromJid(to);
        let authorId = null, chatType = null, messageType = 2;

        if (type === "groupchat") {
            chatType = "group";
            authorId = XMPP.getNodeFromJid(XMPP.getResourceFromJid(from));
            if (!authorId) authorId = XMPP.getResourceFromJid(from);
            if (authorId === currentChatId) {
                toId = fromId;
                fromId = authorId;
                messageType = 1;
            }
        } else if (type === "chat") {
            chatType = "single";
            authorId = XMPP.getNodeFromJid(from);
            if (authorId === currentChatId) messageType = 1;
        }

        return { ...jsonResult, fromId, toId, authorId, body, chatType, messageType };
    }

    parseJSON(str) {
        try {
            return JSON.parse(str);
        } catch (ex) {
            return null;
        }
    }
}